import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import Widget from '../../../components/widget/widget.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import RemoveIcon from '../../../icons/remove-icon';
import StructureService from '../../../services/structure-service';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import {
  DEAL_EXTERNAL_LINKS_LIST_VIEW, DEAL_EXTERNAL_LINKS_ITEM_EDIT, DEAL_EXTERNAL_LINKS_ITEM_CREATE,
  DEAL_EXTERNAL_LINKS_ITEM_DELETE,
} from '../../../constants/entity/permissions';

const DealExternalLinks: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'External Links';
  const WIDGET_NAME: string = 'widget-deal-external-links';
  const COLUMNS_TOTAL: number = 3;

  const {
    isEditMode,
    isNewDeal,
    dealExternalLinksData,
    dealExternalLinksNewItems,
    onAddNewItem,
    onDeleteItem,
    onEditModeButtonClick,
  } = props;

  const [dealExternalLinks, setDealExternalLinks]: [Record<string, DealExternalLinksItem> | null, Function]
    = useState<StateValueDefault>(null);

  const dealExternalLinksListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_EXTERNAL_LINKS_LIST_VIEW,
  ], []);
  const dealExternalLinksItemEditPermissions: string[] = useMemo((): string[] => [
    DEAL_EXTERNAL_LINKS_ITEM_EDIT,
  ], []);
  const dealExternalLinksItemDeletePermissions: string[] = useMemo((): string[] => [
    DEAL_EXTERNAL_LINKS_ITEM_DELETE,
  ], []);
  const dealExternalLinksItemCreatePermissions: string[] = useMemo((): string[] => [
    DEAL_EXTERNAL_LINKS_ITEM_CREATE,
  ], []);

  const formPrefixExternalLinks: string = 'dealData.dealExternalLinksData';
  const isEmpty: boolean = !isEditMode && !isNewDeal && (!dealExternalLinksData || !dealExternalLinksData.length);

  const addNewItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewItem) {
      onAddNewItem({
        id: StructureService.getNewItemId(dealExternalLinksNewItems, dealExternalLinksData),
        name: '',
        link: '',
        description: '',
      });
    }
  }, [dealExternalLinksData, dealExternalLinksNewItems]);

  const deleteItem: Function = useCallback((dealExternalLinkItemId: number): void => {
    if (onDeleteItem) {
      onDeleteItem(dealExternalLinkItemId);
    }

    if (dealExternalLinks) {
      const newDealExternalLinks: Record<string, DealExternalLinksItem> | null = dealExternalLinks;
      delete newDealExternalLinks[dealExternalLinkItemId];
      setDealExternalLinks({ ...newDealExternalLinks });
    }
  }, [dealExternalLinks, dealExternalLinksNewItems]);

  useEffect((): void => {
    if (dealExternalLinksData) {
      const initialDealExternalLinksItems: Record<string, DealExternalLinksItem> = {};

      dealExternalLinksData.forEach((dealExternalLinksItem: DealExternalLinksItem): void => {
        initialDealExternalLinksItems[dealExternalLinksItem.id] = { ...dealExternalLinksItem };
      });

      setDealExternalLinks(initialDealExternalLinksItems);
    }
  }, [dealExternalLinksData, isEditMode]);

  return (
    <CheckAccess isEnabled={isEditMode || isNewDeal} permissions={dealExternalLinksListViewPermissions}>
      {
        !isEmpty && (
          <Widget
            isFullWidth
            isFullHeight
            isEditMode={isEditMode}
            withEditMode={!isNewDeal}
            editPermissions={dealExternalLinksItemEditPermissions}
            widgetName={WIDGET_NAME}
            defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
            onEditModeButtonClick={onEditModeButtonClick}
          >
            <Table>
              <TableBody isPlain withZebraRows withHeaderColumn>
                {
                  [
                    ...((dealExternalLinks && Object.values(dealExternalLinks)) || []),
                    ...(((isEditMode || isNewDeal) && dealExternalLinksNewItems) || []),
                  ].map((dealExternalLinkItem: DealExternalLinksItem): ReactElement => {
                    const isNewItem: boolean = dealExternalLinksNewItems
                      && !!dealExternalLinksNewItems.length && !!dealExternalLinksNewItems.find((
                        dealExternalLinksNewItem: DealExternalLinksItem,
                      ): boolean => (
                        dealExternalLinksNewItem.id === dealExternalLinkItem.id
                      ));
                    const formKeyDealExternalLinkItem: string = FormService.getIndexedFormFieldKey(
                      dealExternalLinkItem.id as number,
                    );

                    return (
                      !isEditMode && !isNewDeal ? (
                        <TableRow key={dealExternalLinkItem.id}>
                          <TableCell>
                            <FilteredLink
                              isExternal
                              link={FormatService.isValidLink(dealExternalLinkItem.link)
                                ? dealExternalLinkItem.link : null}
                            >
                              {dealExternalLinkItem.name}
                            </FilteredLink>
                          </TableCell>
                          <TableCell>
                            {dealExternalLinkItem.description}
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow isActive={isNewItem && !isNewDeal} key={dealExternalLinkItem.id}>
                          <TableCell>
                            <FormFieldText
                              isHidden
                              name={`${formPrefixExternalLinks}[${formKeyDealExternalLinkItem}].id`}
                            />
                            <FormFieldText
                              name={`${formPrefixExternalLinks}[${formKeyDealExternalLinkItem}].name`}
                              placeholder="Name"
                            />
                          </TableCell>
                          <TableCell>
                            <FormFieldText
                              name={`${formPrefixExternalLinks}[${formKeyDealExternalLinkItem}].link`}
                              placeholder="Link"
                            />
                          </TableCell>
                          <TableCell>
                            <FlexContainer>
                              <FormFieldText
                                name={`${formPrefixExternalLinks}[${formKeyDealExternalLinkItem}].description`}
                                placeholder="Description"
                              />
                              <CheckAccess permissions={dealExternalLinksItemDeletePermissions}>
                                <Tooltip title="Delete">
                                  <ActionButton
                                    isInline
                                    withSmallPadding
                                    onClick={(): void => {
                                      deleteItem(dealExternalLinkItem.id);
                                    }}
                                  >
                                    <RemoveIcon />
                                  </ActionButton>
                                </Tooltip>
                              </CheckAccess>
                            </FlexContainer>
                          </TableCell>
                        </TableRow>
                      )
                    );
                  })
                }
                {
                  (isEditMode || isNewDeal) && (
                    <CheckAccess permissions={dealExternalLinksItemCreatePermissions}>
                      <TableRowAddNewItem label="Add New Link" colSpan={COLUMNS_TOTAL} onClick={addNewItem} />
                    </CheckAccess>
                  )
                }
              </TableBody>
            </Table>
          </Widget>
        )
      }
    </CheckAccess>
  );
});

export default DealExternalLinks;
